import React, { useCallback, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { TextInput } from '@epcbuilder/lib/components';
import { AddressResult, PostcodeSearch } from '@epcbuilder/lib/models/properties';
import { getAddressesForPostcode } from '@epcbuilder/lib/network/properties';
import { handleFormErrors, POSTCODE_REGEX } from '@epcbuilder/lib/utils';
import { AxiosErrorData, handleUnknownDetail } from '@epcbuilder/lib/utils/api';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import RegisterModal from './RegisterModal';

const postcodeSearchSchema = yup.object().shape({
  postcode: yup.string().required().matches(POSTCODE_REGEX, 'Please enter a valid postcode'),
});

interface GetStartedProps {
  darkMode?: boolean;
  onSuccess?: (postcode: string, results: AddressResult[]) => void;
}

const GetStarted = ({ darkMode = false, onSuccess }: GetStartedProps) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [results, setResults] = useState<AddressResult[]>([]);

  const defaultValues: PostcodeSearch = {
    postcode: '',
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    getValues,
    clearErrors,
  } = useForm<PostcodeSearch>({
    defaultValues,
    resolver: yupResolver(postcodeSearchSchema),
  });

  const onSubmit: SubmitHandler<PostcodeSearch> = useCallback(
    async (data: PostcodeSearch) => {
      try {
        const response = await getAddressesForPostcode({ postcode: data.postcode.trim() });
        onSuccess?.(data.postcode.trim(), response);
        setResults(response);
        setShowModal(true);
      } catch (error: unknown) {
        const { errors, detail } = error as AxiosErrorData;
        handleFormErrors<PostcodeSearch>(setError, errors);

        switch (detail) {
          case 'No addresses could be found':
          case 'Failed getting address':
            toast.error('There was an error, please check your postcode and try again');
            break;
          default:
            handleUnknownDetail(error);
            break;
        }
      }
    },
    [onSuccess, setError]
  );

  const wrapperClass = `
  flex flex-col gap-2 md:flex-row md:items-center md:justify-between rounded-[10px]
  ${
    darkMode
      ? 'md:border-2 md:border-[#227D72] md:py-2 md:pr-2 md:shadow-md md:bg-[#153542]'
      : 'md:border-2 md:border-[#A2D6CF] md:bg-[#FAFAFA] md:py-2 md:pl-4 md:pr-2'
  }
`;

  const inputClass = `
  border-2 px-4 py-2 md:flex-1 md:border-none sm:bg-transparent md:p-0 w-full
  focus:outline-none focus:ring-0 rounded-[10px] sm:rounded-[0px]
  ${darkMode ? 'md:text-white md:placeholder:text-[#A8B5BA] input-autofill-dark sm:ml-4 border-[#227D72] bg-[#153542]' : 'bg-[#FAFAFA] border-[#A2D6CF]'}
`;

  const buttonClass = `
  flex min-h-10 w-full items-center justify-center rounded-[10px] border-none px-6 py-2 font-bold hover:opacity-90
  md:max-w-[135px] min-w-[147px]
  ${
    darkMode
      ? 'bg-gradient-to-r from-[#F5FFFD] to-[#CAE9E4] text-[#264653] font-nunito md:w-[160px]'
      : 'bg-gradient-to-r from-[#2A9C8E] to-[#153542] text-white'
  }
`;

  return (
    <>
      <form id="postcode-search-form" className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className={wrapperClass}>
          <TextInput
            {...register('postcode')}
            id="Postcode"
            name="postcode"
            title="Your postcode"
            placeholder="Enter your postcode..."
            className={inputClass}
            overrideBaseClassnames={true}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') clearErrors('postcode');
            }}
          />
          <button className={buttonClass} type="submit" disabled={isSubmitting} id="postcode-search-button">
            {isSubmitting ? 'Loading...' : 'Get Started'}
          </button>
        </div>
        {errors.postcode?.message && <p className="text-error mt-1 text-xs">{errors.postcode.message}</p>}
      </form>

      {showModal && (
        <RegisterModal
          postcode={getValues('postcode')}
          initialResults={results}
          onClose={() => setShowModal(false)}
          updateInitialResults={onSubmit}
        />
      )}
    </>
  );
};

export default GetStarted;
